import type {
  GetUploadFileTypesProps,
  UpdateFileStatusInFileListProps,
  UploadFileType,
  UploadFileTypeCategory,
  UploadRcFile,
  UploadType,
} from "./upload-file.type";
import type { UploadFile } from "antd";

import { convertFileToBase64, getFileExtension } from "@/utilities/file";

import {
  UPLOAD_FILE_TYPES_BLACKLIST,
  UPLOAD_FILE_TYPES_WHITELIST,
} from "./upload-file.constant";

export const isFileUploading = (file: UploadFile): boolean => {
  return file.status === "uploading";
};

export const isFileUploaded = (file: UploadFile): boolean => {
  return file.status === "done";
};

export const isFileRemoved = (file: UploadFile): boolean => {
  return file.status === "removed";
};

export const createUploadFileFromFile = async (file: UploadRcFile) => {
  if (!file?.uid) return null;

  const { uid, name, type } = file;
  const url = await convertFileToBase64(file);

  const uploadFile: UploadFile = {
    uid,
    name,
    type,
    url: url as string,
    status: "uploading",
  };

  return uploadFile;
};

export const updateFileStatusInFileList = ({
  files,
  file,
  status,
}: UpdateFileStatusInFileListProps): UploadFile[] => {
  if (!files?.length || !file?.uid) return [];

  return (files ?? []).map((fileItem) =>
    fileItem.uid === file.uid ? { ...file, status } : fileItem
  );
};

export const getFileUploadType = (
  file: UploadRcFile
): UploadFileType | undefined => {
  const ext = getFileExtension(file);
  if (!ext) return undefined;
  return `.${ext}`;
};

export const isUploadFileType = (
  fileType?: string
): fileType is UploadFileType => {
  return fileType ? fileType.startsWith(".") : false;
};

export const isAuthorizedUploadFileType = (
  fileType?: string
): fileType is UploadFileType => {
  if (!isUploadFileType(fileType)) return false;

  if (isUploadFileTypeBlacklisted(fileType)) return false;

  const whitelist = getUploadWhitelistFileTypes("all");

  return whitelist.includes(fileType);
};

export const isUploadFileTypeBlacklisted = (
  fileType: UploadFileType
): boolean => {
  return UPLOAD_FILE_TYPES_BLACKLIST.includes(fileType);
};

export const getUploadAuthorizedFileTypes = ({
  type,
  fileTypes,
}: GetUploadFileTypesProps): UploadFileType[] => {
  const authorizedTypes = (fileTypes ?? []).filter((type) =>
    isAuthorizedUploadFileType(type)
  );

  if (!authorizedTypes.length) {
    const category = getFileTypeCategoryFromType(type);
    return getUploadWhitelistFileTypes(category);
  }

  return authorizedTypes;
};

export const getFileTypeCategoryFromType = (
  type: UploadType
): UploadFileTypeCategory => {
  return type === "image" ? "image" : "all";
};

export const getUploadWhitelistFileTypes = (
  category: UploadFileTypeCategory
): UploadFileType[] => {
  const fileTypes =
    category === "all"
      ? Object.values(UPLOAD_FILE_TYPES_WHITELIST).flat()
      : UPLOAD_FILE_TYPES_WHITELIST[category];

  return fileTypes.filter((fileType) => !isUploadFileTypeBlacklisted(fileType));
};
